/**
 * Per-template send health, from the email provider's own daily statistics.
 *
 * PROVIDER-LEVEL, AND THE CARD SAYS SO. These are Brevo tag aggregates
 * (`template:wedding_*`) across the whole account for the window — they are not
 * per guest and not per book. The per-guest columns on `wedding_invitations`
 * (opened_at, bounced_at) still have no writer; nothing here is derived from
 * them, and nothing infers an open from a click.
 *
 * A RATE THAT WAS NOT MEASURED RENDERS '—'. When nothing was delivered for a
 * tag the provider reports no rate, and the server sends NULL with
 * `health: null`. Zero would be a claim; the dash is the truth. The health
 * thresholds themselves arrive in the payload so this file cannot disagree with
 * the server about what "healthy" means.
 */

import React from 'react';
import {
  Box, Card, CardContent, Chip, Link, Table, TableBody, TableCell, TableHead,
  TableRow, Tooltip, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { num } from '../weddingFormat';
import type { EmailHealth, EmailTemplatesSummary } from '../weddingApi';

interface Props {
  summary: EmailTemplatesSummary | undefined;
  rangeLabel: string;
}

const HEALTH_META: Record<EmailHealth, { label: string; color: 'success' | 'warning' | 'error' }> = {
  good: { label: 'Healthy', color: 'success' },
  watch: { label: 'Watch', color: 'warning' },
  poor: { label: 'Poor', color: 'error' },
};

/** 0.6412 → "64%". NULL → '—', never "0%". */
function rate(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${Math.round(value * 100)}%`;
}

/** Two decimals — a bounce rate rounds to 0% long before it stops mattering. */
function bounceRate(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(2)}%`;
}

function humaniseTemplateKey(key: string): string {
  const spaced = String(key || '').replace(/^wedding[_-]?/, '').replace(/[_-]+/g, ' ').trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const EmailTemplatesSummaryCard: React.FC<Props> = ({ summary, rangeLabel }) => {
  const navigate = useNavigate();
  const tags = summary?.tags || [];
  const totals = summary?.totals || null;

  return (
    <Card sx={{ height: '100%', borderRadius: '16px' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Email templates
          </Typography>
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            onClick={() => navigate('/dashboard/wedding/templates')}
          >
            Open editor →
          </Link>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Sends, opens and clicks per template over {rangeLabel}. Reported by the email provider
          against the template tag, not per guest.
        </Typography>

        {summary && summary.available === false ? (
          <Typography variant="body2" color="text.secondary">
            {summary.reason
              || 'The provider statistics table is not in this database, so there is nothing to report yet.'}
          </Typography>
        ) : tags.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No wedding email was sent in this range.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Template</TableCell>
                <TableCell align="right">Sent</TableCell>
                <TableCell align="right">Opened</TableCell>
                <TableCell align="right">Clicked</TableCell>
                <TableCell align="right">Health</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tags.map((row) => {
                const health = row.health ? HEALTH_META[row.health] : null;
                return (
                  <TableRow key={row.tag}>
                    <TableCell>
                      <Tooltip title={row.tag}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {humaniseTemplateKey(row.template_key)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={`${num(row.delivered).toLocaleString()} delivered`}>
                        <Typography variant="body2">
                          {row.requests === null || row.requests === undefined
                            ? '—'
                            : num(row.requests).toLocaleString()}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{rate(row.open_rate)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{rate(row.click_rate)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      {health ? (
                        <Chip size="small" variant="outlined" color={health.color} label={health.label} />
                      ) : (
                        <Tooltip title="Nothing was delivered for this template in the window, so there is no rate to judge. Unknown, not poor.">
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {totals && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {num(totals.requests).toLocaleString()} total sends in this range · bounce{' '}
            {bounceRate(totals.bounce_rate)}
            {summary?.registry_templates_added_in_range !== null
              && summary?.registry_templates_added_in_range !== undefined
              ? ` · ${summary.registry_templates_added_in_range} template${
                summary.registry_templates_added_in_range === 1 ? '' : 's'} added in this range`
              : ''}
          </Typography>
        )}

        {summary?.registry_available === false && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            The template registry is not in this database, so no count of newly added templates is shown.
          </Typography>
        )}

        {summary?.thresholds && tags.length > 0 && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            Healthy is an open rate at or above {Math.round(summary.thresholds.open_rate_good * 100)}%;
            below {Math.round(summary.thresholds.open_rate_watch * 100)}%, or a bounce rate above{' '}
            {(summary.thresholds.bounce_rate_warn * 100).toFixed(0)}%, reads as poor.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default EmailTemplatesSummaryCard;
